import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faUsersGear,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import swal from "sweetalert";
import { APIMS } from "../../../../../../config/apims";
import { APIUS } from "../../../../../../config/apius";
import ToastError from "../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../NotificationToast/ToastSuccess";

const API_LIMIT = 1000;
const PREVIEW_LIMIT = 12;
const text = (value) => (value == null ? "" : String(value));

const getInitials = (name) => {
  const words = text(name).trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "-";
  return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join("");
};

const normalizeCandidate = (item) => ({
  ...item,
  id: item?.id,
  full_name: item?.nama_lengkap || item?.full_name || item?.name || "-",
  nisn: item?.nisn || "-",
});

export default function GenerateStudentModal({
  show,
  onClose,
  onSuccess,
  semesters = [],
  branches = [],
  activeSemester,
  defaultBranchId = "",
  defaultSchoolId = "",
  headers,
  userLevel,
}) {
  const [filters, setFilters] = useState({
    academicYearId: "",
    academicYearName: "",
    ysb_branch_id: "",
    ysb_school_id: "",
    semesterId: "",
  });
  const [schools, setSchools] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [alreadyGeneratedIds, setAlreadyGeneratedIds] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);
  const [uncheckedIds, setUncheckedIds] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [loadingSchool, setLoadingSchool] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const semesterOptions = useMemo(
    () => [...semesters].sort((a, b) => {
      const yearOrder = text(b?.name_year).localeCompare(text(a?.name_year), "id", { numeric: true });
      return yearOrder || Number(a?.semester) - Number(b?.semester);
    }),
    [semesters],
  );

  const availableCandidates = useMemo(
    () => candidates.filter((item) =>
      !removedIds.includes(String(item?.id)) &&
      !alreadyGeneratedIds.includes(String(item?.id))
    ),
    [candidates, removedIds, alreadyGeneratedIds],
  );

  const selectedCandidates = useMemo(
    () => availableCandidates.filter((item) => !uncheckedIds.includes(String(item?.id))),
    [availableCandidates, uncheckedIds],
  );

  const matchingCandidates = useMemo(() => {
    const keyword = candidateSearch.trim().toLocaleLowerCase("id");
    return keyword
      ? availableCandidates.filter((item) => text(item?.full_name).toLocaleLowerCase("id").includes(keyword))
      : availableCandidates;
  }, [candidateSearch, availableCandidates]);

  const visibleCandidates = showAll || candidateSearch.trim()
    ? matchingCandidates
    : matchingCandidates.slice(0, PREVIEW_LIMIT);

  useEffect(() => {
    if (!show) return;

    const active = activeSemester || semesters.find((item) => Number(item?.status) === 1) || semesters[0];

    const yearName = active?.name_year || "";

    setFilters({
      academicYearId:
        active?.ysb_academic_year_id ||
        active?.academic_year_id ||
        active?.academic_year?.id ||
        active?.academicYear?.id ||
        "",
      academicYearName: yearName,
      ysb_branch_id: defaultBranchId || branches[0]?.branch_code || "",
      ysb_school_id: defaultSchoolId || "",
      semesterId: active?.id == null ? "" : String(active.id),
    });
    setCandidates([]);
    setAlreadyGeneratedIds([]);
    setRemovedIds([]);
    setUncheckedIds([]);
    setShowAll(false);
    setCandidateSearch("");
  }, [activeSemester, branches, defaultBranchId, defaultSchoolId, semesters, show]);

  useEffect(() => {
    if (!show || !filters.ysb_branch_id) {
      setSchools([]);
      return;
    }

    let active = true;

    const loadSchools = async () => {
      setLoadingSchool(true);
      try {
        const response = await APIMS.get(
          `/api/access/schools?page=1&limit=${API_LIMIT}&ascending=1&branch=${filters.ysb_branch_id}&level=${userLevel}`,
          headers,
        );
        let data = response?.data?.data || [];
        if (["developer", "superadmin"].includes(userLevel)) {
          data = data.filter((item) => item?.ysb_branch_id === filters.ysb_branch_id);
        }
        if (filters.ysb_branch_id === "BKS") {
          data = data.filter((item) => item?.school_code !== "BASEMENT_BEKASI");
        }
        data.sort((a, b) => text(a?.school_name).localeCompare(text(b?.school_name), "id", { numeric: true }));
        if (!active) return;
        setSchools(data);
        setFilters((old) => ({
          ...old,
          ysb_school_id: data.some((item) => item?.school_code === old.ysb_school_id)
            ? old.ysb_school_id
            : data[0]?.school_code || "",
        }));
      } catch (error) {
        if (!active) return;
        setSchools([]);
        setFilters((old) => ({ ...old, ysb_school_id: "" }));
        swal({
          title: "Gagal",
          text: error?.response?.data?.message || "Gagal memuat daftar sekolah",
          icon: "error",
        });
      } finally {
        if (active) setLoadingSchool(false);
      }
    };
    loadSchools();
    return () => { active = false; };
  }, [filters.ysb_branch_id, headers, show, userLevel]);

  useEffect(() => {
    if (!show || !filters.academicYearId || !filters.semesterId || !filters.ysb_branch_id || !filters.ysb_school_id) {
      setCandidates([]);
      setAlreadyGeneratedIds([]);
      return;
    }

    let active = true;

    const loadCandidates = async () => {
      setLoadingCandidates(true);
      setCandidates([]);
      setAlreadyGeneratedIds([]);
      setRemovedIds([]);
      setUncheckedIds([]);
      setShowAll(false);
      try {
        const yearSemesters = semesters.filter((semester) =>
          semester?.name_year === filters.academicYearName ||
          String(semester?.ysb_academic_year_id || semester?.academic_year_id || semester?.academic_year?.id || semester?.academicYear?.id) === String(filters.academicYearId)
        );
        const [response, ...admittedResponses] = await Promise.all([
          APIUS.post(
          "/api/student-admitted/candidates",
          {
            ysb_academic_year_id: filters.academicYearId,
            ysb_semester_id: filters.semesterId,
            ysb_branch_id: filters.ysb_branch_id,
            ysb_school_id: filters.ysb_school_id,
          },
          headers,
          ),
          ...yearSemesters.map((semester) => APIUS.post("/api/student-admitted", {
            ysb_academic_year_id: filters.academicYearId,
            ysb_semester_id: semester.id,
            ysb_branch_id: filters.ysb_branch_id,
            ysb_school_id: filters.ysb_school_id,
          }, headers)),
        ]);
        if (!active) return;
        const admitted = admittedResponses.flatMap((result) => result?.data?.data || []);
        setAlreadyGeneratedIds([...new Set(admitted.map((item) => item?.id).filter((id) => id != null).map(String))]);
        setCandidates((response?.data?.data || []).map(normalizeCandidate));
      } catch (error) {
        if (!active) return;
        setCandidates([]);
        setAlreadyGeneratedIds([]);
        swal({
          title: "Gagal",
          text: error?.response?.data?.message || "Gagal mengambil kandidat siswa",
          icon: "error",
        });
      } finally {
        if (active) setLoadingCandidates(false);
      }
    };
    loadCandidates();
    return () => { active = false; };
  }, [filters.academicYearId, filters.academicYearName, filters.semesterId, filters.ysb_branch_id, filters.ysb_school_id, headers, semesters, show]);

  const handleSubmit = async () => {
    if (selectedCandidates.length === 0 || !filters.semesterId) return;
    // const userId = localStorage.getItem("id_admin");
    const userId = localStorage.getItem("id_admin")?.replace(/[|&;$%@"<>()+,]/g, "");
    if (!userId) {
      swal({ title: "Gagal", text: "ID pengguna tidak ditemukan. Silakan login ulang.", icon: "error" });
      return;
    }

    setSubmitting(true);
    try {
      const studentIds = selectedCandidates.map((item) => item?.id).filter(Boolean);
      await APIUS.post(
        "/api/student-admitted/generate",
        {
          ysb_academic_year_id: filters.academicYearId,
          ysb_semester_id: filters.semesterId,
          ysb_branch_id: filters.ysb_branch_id,
          ysb_school_id: filters.ysb_school_id,
          student: studentIds,
          user_id: userId,
        },
        headers,
      );

      onSuccess?.({ candidates: selectedCandidates, filters });
      onClose();
      // swal({
      //   title: "Berhasil",
      //   text: `${selectedCandidates.length} siswa berhasil digenerate`,
      //   icon: "success",
      //   timer: 1800,
      //   buttons: false,
      // });
      ToastSuccess.fire({
          icon: "success",
          title: `${selectedCandidates.length} siswa berhasil digenerate`,
        });
    } catch (error) {
      // swal({
      //   title: "Gagal",
      //   text: error?.response?.data?.message || "Siswa gagal digenerate",
      //   icon: "error",
      // });
      ToastError.fire({
        icon: "error",
        title: error.response?.data?.message || "Gagal menyimpan data",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const safeStr = (value) =>
    value === null || value === undefined ? "" : String(value);

  const toTitleCase = (value) => {
    const text = safeStr(value).toLowerCase().trim();

    if (!text) {
      return "";
    }

    return text.replace(/\w\S*/g, (word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
  };

  return (
    <div className="student-generate-overlay" role="presentation">
      <div 
        className="student-generate-modal" 
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="generate-student-title"
      >
        <div className="student-generate-header">
          <div>
            <h5 id="generate-student-title">Generate Siswa</h5>
            </div>
          <button type="button" className="student-generate-close" aria-label="Tutup modal" onClick={onClose} disabled={submitting}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="student-generate-body">
          <div className="student-generate-filters">
            <div className="sims-filter-field">
              <label className="sims-filter-label" htmlFor="generate-semester">Semester</label>
              <select
                id="generate-semester"
                className="sims-filter-select"
                value={filters.semesterId}
                disabled
              >
                <option value="" disabled>Pilih Semester</option>
                {semesterOptions.map((item) => (
                  <option key={item?.id} value={String(item?.id)}>
                    {item?.name_year} (Semester {item?.semester}){Number(item?.status) === 1 ? " • Aktif" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="sims-filter-field">
              <label className="sims-filter-label">Cabang</label>
              <select 
                className="sims-filter-select" 
                value={filters.ysb_branch_id} 
                onChange=
                  {(event) => setFilters((old) => ({ ...old, ysb_branch_id: event.target.value, ysb_school_id: "" }))}
              >
                <option value="" disabled>Pilih Cabang</option>
                {branches.map((item) => 
                  <option 
                    key={item?.branch_code} 
                    value={item?.branch_code}
                  > 
                    {item?.branch_name}
                  </option>)}
              </select>
            </div>

            <div className="sims-filter-field">
              <label className="sims-filter-label">Sekolah</label>
              <select 
                className="sims-filter-select" 
                value={filters.ysb_school_id} 
                disabled={loadingSchool || !filters.ysb_branch_id} 
                onChange=
                  {(event) => setFilters((old) => ({ ...old, ysb_school_id: event.target.value }))}
              >
                <option value="" disabled>
                  {loadingSchool ? "Memuat sekolah..." : "Pilih Sekolah"}
                </option>
                {schools.map((item) => 
                  <option 
                    key={item?.school_code} 
                    value={item?.school_code}
                  >
                    {item?.school_name}
                  </option>
                )}
              </select>
            </div>
          </div>

          <div className="student-candidate-card">
            <div className="student-candidate-summary">
              <div>
                <span className="student-candidate-label">
                  Siswa baru tersedia
                </span>
                <strong>{availableCandidates.length} siswa</strong>
              </div>

              {!candidateSearch.trim() && availableCandidates.length > PREVIEW_LIMIT && (
                <button 
                  type="button" 
                  style={{fontSize: "14px"}} className="student-show-button" 
                  onClick={() => setShowAll((old) => !old)}>
                    {showAll ? "Tutup daftar" : "Lihat semua"}
                  <FontAwesomeIcon icon={showAll ? faChevronUp : faChevronDown} />
                </button>
              )}
            </div>
            <div className="student-candidate-search">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input
                type="search"
                aria-label="Cari nama siswa baru"
                placeholder="Cari nama siswa..."
                value={candidateSearch}
                onChange={(event) => setCandidateSearch(event.target.value)}
              />
            </div>

            {loadingCandidates ? (
              <div className="student-candidate-empty">
                <PulseLoader color="#3273dc" size={8} />
              </div>
            ) : !filters.semesterId ? (
              <div className="student-candidate-empty">
                Pilih semester terlebih dahulu.
              </div>
            ) : availableCandidates.length === 0 ? (
              <div className="student-candidate-empty">
                Tidak ada kandidat siswa baru pada filter ini.
              </div>
            ) : matchingCandidates.length === 0 ? (
              <div className="student-candidate-empty">
                Nama siswa tidak ditemukan.
              </div>
            ) : (
              <>
                <label className="student-select-all">
                  <input 
                    type="checkbox" 
                    checked={selectedCandidates.length === availableCandidates.length} 
                    onChange=
                      {(event) => setUncheckedIds(event.target.checked ? [] : availableCandidates.map((item) => String(item.id)))} 
                  />
                    Pilih semua siswa
                </label>

                <div 
                  className={`student-candidate-list ${showAll || candidateSearch.trim() ? "is-expanded" : ""}`}>
                  {visibleCandidates.map((student) => (
                    <div 
                      key={student.id}
                      className="student-candidate-item"
                    >
                      <input 
                        className="student-candidate-checkbox"
                        type="checkbox" 
                        aria-label={`Pilih ${student.full_name}`}
                        checked={!uncheckedIds.includes(String(student.id))}
                        onChange=
                          {(event) => setUncheckedIds((old) => event.target.checked ? old.filter((id) => id !== String(student.id)) : [...old, String(student.id)])}
                      />
                      <span className="student-candidate-avatar">{getInitials(student.full_name)}</span>

                      <div className="student-candidate-info">
                        <strong>{toTitleCase(student.full_name)}</strong>
                        <span>NISN {student.nisn}</span>
                      </div>

                      <button 
                        type="button" 
                        className="student-remove-button" 
                        title="Hapus dari daftar generate" 
                        aria-label={`Hapus ${student.full_name}`} 
                        onClick={() => setRemovedIds((old) => [...old, String(student.id)])}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="student-generate-footer">
          <button 
            type="button" 
            className="student-cancel-button" 
            onClick={onClose} 
            disabled={submitting}
          >
            Batal
          </button>
          <button 
            type="button" 
            className="student-submit-button" 
            disabled= {loadingCandidates || submitting || selectedCandidates.length === 0 || !filters.semesterId} 
            onClick={handleSubmit}
          >
            {submitting ? 
              <PulseLoader color="#ffffff" size={7} /> 
              : 
              <>
                <FontAwesomeIcon icon={faUsersGear} /> 
                Generate {selectedCandidates.length} Siswa
              </>
              }
          </button>
        </div>
      </div>
    </div>
  );
}
